import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Seo } from "@/lib/seo";

const STATUSES = ["New", "Reviewing", "Approved", "Rejected", "Contacted"];
const statusColor = {
  New: "bg-astro-blue/15 text-astro-light",
  Reviewing: "bg-amber-500/15 text-amber-300",
  Approved: "bg-emerald-500/15 text-emerald-300",
  Rejected: "bg-red-500/15 text-red-300",
  Contacted: "bg-slate-500/20 text-slate-300",
};

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

export default function Submissions({ kind }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const isBeta = kind === "beta";
  const endpoint = isBeta ? "beta" : "contact";
  const title = isBeta ? "Private Beta Requests" : "Contact Enquiries";

  const { data, isLoading } = useQuery({
    queryKey: [`admin-${endpoint}`],
    queryFn: async () => (await api.get(`/admin/${endpoint}`)).data,
  });

  const mutate = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/admin/${endpoint}/${id}/status`, { status, note })).data,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: [`admin-${endpoint}`] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      setSelected(updated);
      setNote("");
      toast.success(`Status updated to ${updated.status}`);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const rows = data || [];

  return (
    <>
      <Seo title={title} description="" path={`/admin/${endpoint}`} />
      <h1 className="font-heading text-3xl font-black text-white">{title}</h1>
      <p className="mt-2 text-silver/60">{rows.length} total submissions.</p>

      <div className="mt-8 overflow-hidden rounded-2xl border border-navy-border bg-navy-surface">
        {isLoading ? (
          <div className="p-10 text-center text-silver/50">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-silver/50" data-testid={`${endpoint}-empty`}>No submissions yet.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-navy-border text-xs uppercase tracking-wider text-silver/50">
              <tr>
                <th className="px-5 py-4">Name</th>
                <th className="hidden px-5 py-4 md:table-cell">Organization</th>
                <th className="hidden px-5 py-4 lg:table-cell">Received</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => { setSelected(r); setNote(""); }}
                  data-testid={`${endpoint}-row-${r.id}`}
                  className="cursor-pointer border-b border-navy-border/50 transition-colors hover:bg-navy"
                >
                  <td className="px-5 py-4">
                    <p className="font-medium text-white">{r.name}</p>
                    <p className="text-xs text-silver/50">{r.email}</p>
                  </td>
                  <td className="hidden px-5 py-4 text-silver/70 md:table-cell">{r.organization || "—"}</td>
                  <td className="hidden px-5 py-4 text-silver/50 lg:table-cell">{fmt(r.created_at)}</td>
                  <td className="px-5 py-4">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusColor[r.status]}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="submission-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSelected(null)} />
          <div className="relative h-full w-full max-w-md overflow-y-auto border-l border-navy-border bg-navy-surface p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-heading text-xl font-bold text-white">{selected.name}</h2>
                <a href={`mailto:${selected.email}`} className="text-sm text-astro-light">{selected.email}</a>
              </div>
              <button onClick={() => setSelected(null)} className="text-silver/60 hover:text-white" data-testid="drawer-close"><X className="h-5 w-5" /></button>
            </div>

            <dl className="mt-6 space-y-3 text-sm">
              {[
                ["Organization", selected.organization],
                ["Role", selected.role],
                ["Sector", selected.sector],
                ["Country", selected.country],
                ["Deployment", selected.deployment],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-navy-border/50 pb-2">
                  <dt className="text-silver/50">{k}</dt>
                  <dd className="text-right text-silver/90">{v}</dd>
                </div>
              ))}
            </dl>

            {(selected.message || selected.use_case) && (
              <div className="mt-5 rounded-xl border border-navy-border bg-navy p-4">
                <p className="text-xs uppercase tracking-wider text-silver/50">{isBeta ? "Use case" : "Message"}</p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-silver/80">{selected.message || selected.use_case}</p>
              </div>
            )}

            {/* Status workflow */}
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider text-silver/50">Update status</p>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note (audit trail)"
                data-testid="status-note"
                className="mt-3 w-full rounded-lg border border-navy-border bg-navy px-3 py-2 text-sm text-white focus:border-astro-blue focus:outline-none"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    disabled={mutate.isPending}
                    onClick={() => mutate.mutate({ id: selected.id, status: s })}
                    data-testid={`set-status-${s}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${selected.status === s ? statusColor[s] : "border border-navy-border text-silver/60 hover:text-white"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Audit trail */}
            <div className="mt-8">
              <p className="text-xs uppercase tracking-wider text-silver/50">Audit trail</p>
              <ol className="mt-4 space-y-4">
                {(selected.history || []).slice().reverse().map((h, i) => (
                  <li key={i} className="flex gap-3">
                    <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-astro-light" />
                    <div>
                      <p className="text-sm text-white"><span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${statusColor[h.status]}`}>{h.status}</span></p>
                      {h.note && <p className="mt-1 text-sm text-silver/70">{h.note}</p>}
                      <p className="mt-0.5 text-xs text-silver/40">{fmt(h.at)} · {h.by}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
